import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  EventEmitter,
  HostListener,
  inject,
  OnDestroy,
  Output,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import { map, Subject } from 'rxjs';
import { SIDE_NAV_STATE } from 'src/app/core/enums/sidenav-state.enum';
import { NavigationSetup } from 'src/app/core/services/navigation-items-service/navigation-setup';
import { UserService } from 'src/app/core/services/user-service/user.service';
import { StartPageSearchBarComponent } from '../../../../pages/start-page/components/start-page-search-bar/start-page-search-bar.component';
import { DragElementEnum } from '../../../../pages/start-page/enums/drag-element.enum';
import { DragDropService } from '../../../../pages/start-page/services/drag-drop.service';
import { NavigationItem } from '../../../interfaces/navigation-items';
import { NavigationItemsService } from '../../../services/navigation-items-service/navigation-items.service';
import { CurrentNavigationItemService } from '../../../services/navigation-service/current-navigation-item.service';
import { SideNavigationService } from '../../../services/navigation-service/side-navigation.service';
import { PlatformService } from '../../../services/platform-service/platform.service';
import { BoschLogoComponent } from "../../bosch-logo/bosch-logo.component";
import { FooterNavigationComponent } from "../footer-side-navigation/footer-navigation.component";
import { IdeClientComponent } from './ide-client/ide-client.component';
import { SideNavigationItemComponent } from './side-navigation-item/side-navigation-item.component';

@Component({
  selector: 'app-header',
  imports: [
    CommonModule,
    RouterLink,
    SideNavigationItemComponent,
    IdeClientComponent,
    StartPageSearchBarComponent,
    FooterNavigationComponent,
    BoschLogoComponent
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderComponent implements OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);

  @Output() toggledSidenav = new EventEmitter<boolean>();

  destroy$: Subject<boolean> = new Subject<boolean>();

  currentNavigationItem = this.currentNavigationItemService.currentNavigationItem;

  sidenav = toSignal(
    this.route.queryParamMap.pipe(
      map(params => params.get('sidenav') ?? undefined)
    )
  );

  isExpandSideNav = computed<boolean>(() => {
    const currentPageName = this.currentNavigationItem().name;

    if(currentPageName !== NavigationSetup.APP_DETAIL_PAGE.name 
      && currentPageName !== NavigationSetup.COLLECTION_DETAIL_PAGE.name) return false;

    return this.sidenav() === SIDE_NAV_STATE.EXPANDED
  })
  
  constructor(
    private readonly router: Router,
    protected platformService: PlatformService,
    protected userService: UserService,
    protected currentNavigationItemService: CurrentNavigationItemService,
    protected sideNavigationService: SideNavigationService,
    protected navigationItems: NavigationItemsService,
    protected route: ActivatedRoute,
    private readonly dragDropService: DragDropService,
  ) {
    this.addRouterEvent();

    effect(() => {
      if(this.isExpandSideNav() && !this.platformService.isMobile() && !this.platformService.isSmallMobileDevice()) {
        this.sideNavigationService.openSideNav();
      }
    })
  }

  ngOnDestroy(): void {
    this.destroy$.next(false);
    this.destroy$.complete();
  }

  currentNavigationOnStartPage() {
    return (
      this.currentNavigationItemService.currentNavigationItem().name ===
      this.navigationItems.getStartPage().name
    );
  }

  toggleSideNav(): void {
    this.sideNavigationService.toggleSideNav();
  }

  onSideNavigationMouseEnter(): void {
    this.dragDropService.updateDropContainerIfDraggingSomething(DragElementEnum.NOT_ALLOWED);
  }

  onSideNavigationMouseLeave(): void {
    this.dragDropService.updateDropContainerIfDraggingSomething(DragElementEnum.NOTHING);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.cdr.markForCheck();
  }

  private addRouterEvent(): void {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentNavigationItemService.set(this.getCurrentNavigationItemByPathName(
          this.router.routerState.snapshot.root.firstChild?.routeConfig
            ?.path ?? ''
        ))

        if (this.platformService.isMobile()) {
          this.sideNavigationService.closeSideNav();
        }
      }
    });
  }

  private getCurrentNavigationItemByPathName(pathName: string): NavigationItem {
    const navItem = this.navigationItems
      .getNavigationItems()
      .find((item) => item.path === pathName);
    return navItem ?? this.navigationItems.getStartPage();
  }


}
